import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, Calendar, ArrowRight, SortAsc, X, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SEO from '../components/SEO';
import { BreadcrumbSchema } from '../components/Schema';
import SocialShare from '../components/SocialShare';
import BookmarkButton from '../components/BookmarkButton';
import LazyImage from '../components/LazyImage';
import BannerAd from '../components/BannerAd';
import { calculateReadingTime, formatReadingTime } from '../utils/readingTime';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [searchParams] = useSearchParams();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState('date-desc');
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts();
    fetchCategories();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/posts?published_only=true&limit=100`);
      // Handle both paginated and non-paginated responses
      const posts = response.data.posts || response.data;
      setBlogPosts(posts);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/categories`);
      setCategories(['All', ...response.data.categories]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const toggleCategory = (category) => {
    if (category === 'All') {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(prev => 
        prev.includes(category) 
          ? prev.filter(c => c !== category)
          : [...prev, category]
      );
    }
  };

  const filteredAndSortedPosts = blogPosts
    .filter(post => {
      const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(post.category);
      const matchesSearch = searchQuery === '' || 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.content?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.date) - new Date(a.date);
        case 'date-asc':
          return new Date(a.date) - new Date(b.date);
        case 'title-asc':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });

  return (
    <div className="min-h-screen">
      <SEO 
        title="Truck Safety Blog - Accident Prevention & Driver Training Resources"
        description="Expert articles on truck accident prevention, driver safety training, DOT compliance, and legal protection. Stay informed with the latest trucking industry safety news and best practices."
        canonical="/blog"
        keywords="truck safety blog, accident prevention articles, driver training resources, DOT compliance news, trucking safety tips, fleet management blog"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Blog', path: '/blog' }
      ]} />
      
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Safety & Legal <span className="text-gradient">Knowledge Hub</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Expert insights on accident prevention, compliance, and legal protection for trucking professionals.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Sort Bar */}
      <section className="py-8 bg-gray-900 border-b border-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles by title, excerpt, or content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-[200px] h-12 bg-gray-800 border-gray-700 text-white">
                <SortAsc className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="date-desc" className="text-white hover:bg-gray-700">Newest First</SelectItem>
                <SelectItem value="date-asc" className="text-white hover:bg-gray-700">Oldest First</SelectItem>
                <SelectItem value="title-asc" className="text-white hover:bg-gray-700">Title (A-Z)</SelectItem>
                <SelectItem value="title-desc" className="text-white hover:bg-gray-700">Title (Z-A)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-6 bg-gray-900 border-b border-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-2 mb-3">
            <Filter className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-400">
              Filter by Category {selectedCategories.length > 0 && `(${selectedCategories.length} selected)`}:
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const isSelected = category === 'All' ? selectedCategories.length === 0 : selectedCategories.includes(category);
              return (
                <Button
                  key={category}
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleCategory(category)}
                  className={isSelected 
                    ? "bg-orange-500 hover:bg-orange-600 text-white" 
                    : "border-gray-700 text-gray-300 hover:border-orange-500 hover:text-orange-400"}
                >
                  {category}
                  {selectedCategories.includes(category) && category !== 'All' && (
                    <X className="ml-2 h-3 w-3" />
                  )}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="section-padding bg-gray-950">
        <div className="container mx-auto px-4">
          {/* Header Banner Ad */}
          <BannerAd position="header" page="blog" className="mb-6" />
          
          <div className="mb-6">
            <p className="text-gray-400">
              Showing {filteredAndSortedPosts.length} {filteredAndSortedPosts.length === 1 ? 'article' : 'articles'}
              {selectedCategories.length > 0 && ` in ${selectedCategories.join(', ')}`}
            </p>
          </div>
          
          {filteredAndSortedPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAndSortedPosts.map((post) => (
                <Card key={post.id} className="card-hover bg-gray-800 border-gray-700 overflow-hidden" data-testid={`blog-card-${post.id}`}>
                  <Link to={`/blog/${post.id}`}>
                    <div className="aspect-video overflow-hidden">
                      <LazyImage 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full"
                      />
                    </div>
                  </Link>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <span className="badge badge-orange">{post.category}</span>
                      <div className="flex items-center gap-2">
                        <BookmarkButton post={post} variant="icon" />
                        <div className="flex items-center text-xs text-gray-400">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        <div className="flex items-center text-xs text-gray-400">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatReadingTime(calculateReadingTime(post.content))}
                        </div>
                      </div>
                    </div>
                    <Link to={`/blog/${post.id}`}>
                      <CardTitle className="text-white text-xl hover:text-orange-400 transition-colors cursor-pointer">
                        {post.title}
                      </CardTitle>
                    </Link>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400 mb-4">
                      {post.excerpt}
                    </CardDescription>
                    <div className="flex items-center justify-between">
                      <Link to={`/blog/${post.id}`}>
                        <Button variant="link" className="text-orange-400 hover:text-orange-300 p-0">
                          Read Full Article
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <SocialShare 
                        url={`${window.location.origin}/blog/${post.id}`}
                        title={post.title}
                        description={post.excerpt}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No articles found matching your criteria.</p>
              <Button 
                variant="outline" 
                className="mt-4 border-orange-500 text-orange-400 hover:bg-orange-500/10"
                onClick={() => { setSelectedCategories([]); setSearchQuery(''); }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
